/**
 * Eén weg naar de winkelwagen voor alle Smokkelaar-knoppen (README §5.4).
 *
 * Doet precies wat Horizons productformulier doet (assets/product-form.js):
 *  1. kondig de wijziging aan met een CartLinesUpdateEvent (promise nog open),
 *  2. POST naar cart/add.js of cart/update.js mét `sections` van elke
 *     cart-items-component op de pagina,
 *  3. haal cart.js op en los de promise op met { cart, detail: { sections } }.
 *
 * Daardoor reageert alles wat al naar dat event luistert: cart-items-component
 * morpht lade en pagina uit `sections`, cart-drawer-component[auto-open] gaat
 * open bij action 'add', cart-icon werkt de teller bij en het productformulier
 * zijn data-cart-quantity. Geen renderSection meer nodig.
 *
 * Statiegeld en cadeauverpakking tellen niet als artikel: de teller krijgt
 * item_count zonder die hulpregels, dezelfde regel als in cart-bubble.liquid.
 *
 * Gebruik vanuit JS:
 *   import { voegToe, zetAantallen, heeftLade } from '@theme/smokkelaar-cart';
 *   await voegToe([{ id: 123, quantity: 1 }], { bron: knop });
 *   await zetAantallen({ 123: 0, 456: 1 }, { bron: el, attributes: { cadeau: 'ja' } });
 *
 * Of zonder eigen JS:
 *   <button data-smokkelaar-toevoegen="123" data-context="cart">…</button>
 *   <form data-smokkelaar-ajax action="{{ routes.cart_add_url }}" method="post">  (werkt ook zonder JS)
 */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const HULPREGELS = ['statiegeld', 'cadeauverpakking'];

/**
 * Winkelregels uit de thema-instellingen (JSON-blok #smokkelaar-regels in
 * layout/theme.liquid), zodat sixpack-stand en Smokkelroute met dezelfde
 * getallen rekenen als de Liquid. Ontbreekt het blok, dan de standaarden.
 */
export const regels = (() => {
  const standaard = { sixpackAantal: 6, sixpackKorting: 5, statiegeldBlikCent: 15, statiegeldFlesCent: 10, verzendkostenCent: 795, gratisVerzendingCent: 6000 };
  try {
    const uitThema = JSON.parse(document.getElementById('smokkelaar-regels')?.textContent || '{}');
    for (const sleutel of Object.keys(standaard)) {
      if (Number.isFinite(uitThema[sleutel])) standaard[sleutel] = uitThema[sleutel];
    }
  } catch {
    /* kapot blok: standaarden */
  }
  return standaard;
})();

/** Sixpackkorting in centen: Shopify rekent per fles en kapt af op de cent, niet over het totaal. */
export const sixpackKortingCent = (prijzen) =>
  prijzen.reduce((som, prijs) => som + Math.floor((prijs * regels.sixpackKorting) / 100), 0);

function route(naam, standaard) {
  const root = window.Shopify?.routes?.root || '/';
  return window.Theme?.routes?.[naam] || `${root}${standaard}`;
}

/** Is er een lade op deze pagina? Zo niet (winkelwagenpagina, of cart_type = page), dan naar /cart. */
export function heeftLade() {
  return Boolean(document.querySelector('cart-drawer-component'));
}

export function naarWinkelwagen() {
  location.href = route('cart_url', 'cart');
}

function sectieIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return [...ids].join(',');
}

async function post(adres, body) {
  const res = await fetch(adres, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  // De Ajax API geeft bij een fout 4xx met { status, message, description }.
  if (!res.ok || data.status) {
    const fout = new Error(data.description || data.message || `Winkelwagen: ${res.status}`);
    fout.data = data;
    throw fout;
  }
  return data;
}

async function haalWagen() {
  const res = await fetch(`${route('cart_url', 'cart')}.js`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`cart.js: ${res.status}`);
  return res.json();
}

function aantalArtikelen(wagen) {
  const hulp = wagen.items.filter((i) => HULPREGELS.includes(i.handle)).reduce((s, i) => s + i.quantity, 0);
  return wagen.item_count - hulp;
}

/**
 * Kern: aankondigen, versturen, oplossen.
 * @param {object} o
 * @param {'add'|'update'} o.action
 * @param {'product'|'cart'|'dialog'} o.context
 * @param {Array<object>} o.lines  regels voor het event (merchandiseId+quantity bij add, id+quantity bij update)
 * @param {EventTarget} [o.bron]  element dat het event verstuurt; het bubbelt naar document
 * @param {() => Promise<object>} o.verzoek  doet de POST en geeft het Ajax-antwoord (met `sections`) terug
 * @returns {Promise<object>} de winkelwagen (cart.js) na de wijziging
 */
async function verstuur({ action, context, lines, bron, verzoek }) {
  const doel = bron ?? document;
  const uitgesteld = CartLinesUpdateEvent.createPromise();
  doel.dispatchEvent(new CartLinesUpdateEvent({ action, context, lines, promise: uitgesteld.promise }));

  try {
    const antwoord = await verzoek();
    const wagen = await haalWagen();
    const aantal = aantalArtikelen(wagen);
    uitgesteld.resolve({
      // totalQuantity zonder hulpregels, dan hoeft cart-icon niets na te rekenen.
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse({ ...wagen, item_count: aantal }),
      detail: {
        sections: antwoord.sections,
        didError: false,
        items: wagen.items,
        itemCount: aantal,
        source: 'smokkelaar-cart',
      },
    });
    return wagen;
  } catch (fout) {
    doel.dispatchEvent(
      new CartErrorEvent({ error: fout.message, code: fout.data ? 'INVALID' : 'SERVICE_UNAVAILABLE' })
    );
    // Luisteraars wachten op de promise: oplossen met didError, dan blijft de lade dicht.
    // Ook hier het aantal zonder hulpregels, anders springt de teller naar item_count.
    haalWagen()
      .then((wagen) => {
        const aantal = aantalArtikelen(wagen);
        uitgesteld.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse({ ...wagen, item_count: aantal }),
          detail: { didError: true, items: wagen.items, itemCount: aantal, source: 'smokkelaar-cart' },
        });
      })
      .catch(uitgesteld.reject);
    throw fout;
  }
}

/**
 * Zet regels in de wagen (cart/add.js).
 * @param {Array<{id: number|string, quantity?: number|string, properties?: object}>} items
 * @param {{ bron?: EventTarget, context?: 'product'|'cart'|'dialog' }} [opties]
 */
export function voegToe(items, { bron, context = 'product' } = {}) {
  const regels = items.map((i) => ({
    id: Number(i.id),
    quantity: Number(i.quantity) || 1,
    ...(i.properties ? { properties: i.properties } : {}),
  }));
  return verstuur({
    action: 'add',
    context,
    lines: regels.map((r) => ({ merchandiseId: String(r.id), quantity: r.quantity })),
    bron,
    verzoek: () => post(route('cart_add_url', 'cart/add.js'), { items: regels, sections: sectieIds() }),
  });
}

/**
 * Zet aantallen per variant (cart/update.js): 0 haalt weg, een variant die er
 * nog niet in zit wordt toegevoegd. Optioneel in dezelfde call attributen.
 * @param {Record<string, number>} updates  variant-id → aantal
 * @param {{ bron?: EventTarget, context?: 'product'|'cart'|'dialog', attributes?: Record<string, string> }} [opties]
 */
export function zetAantallen(updates, { bron, context = 'cart', attributes } = {}) {
  const body = { updates, sections: sectieIds() };
  if (attributes) body.attributes = attributes;
  return verstuur({
    action: 'update',
    context,
    lines: Object.entries(updates).map(([id, quantity]) => ({ id: String(id), quantity: Number(quantity) })),
    bron,
    verzoek: () => post(route('cart_update_url', 'cart/update.js'), body),
  });
}

/* ---- Knoppen en formulieren zonder eigen JS ---------------------------- */

function bezig(knop, aan) {
  if (!(knop instanceof HTMLButtonElement)) return;
  knop.disabled = aan;
  knop.classList.toggle('is-bezig', aan);
}

async function klaar(knop, werk) {
  bezig(knop, true);
  try {
    await werk();
    if (!heeftLade()) naarWinkelwagen();
  } catch (fout) {
    console.warn('[smokkelaar-cart]', fout);
  } finally {
    bezig(knop, false);
  }
}

// <button data-smokkelaar-toevoegen="VARIANT" [data-aantal="1"] [data-context="cart"]>
// Gedelegeerd op document, dus overleeft het herrenderen van de lade.
document.addEventListener('click', (event) => {
  const knop = event.target instanceof Element && event.target.closest('[data-smokkelaar-toevoegen]');
  if (!knop) return;
  event.preventDefault();
  klaar(knop, () =>
    voegToe([{ id: knop.dataset.smokkelaarToevoegen, quantity: knop.dataset.aantal }], {
      bron: knop,
      context: knop.dataset.context || 'product',
    })
  );
});

// <form data-smokkelaar-ajax action="{{ routes.cart_add_url }}" method="post"> met id + quantity.
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-smokkelaar-ajax')) return;
  event.preventDefault();
  const data = new FormData(form);
  klaar(form.querySelector('button[type="submit"]'), () =>
    voegToe([{ id: data.get('id'), quantity: data.get('quantity') }], { bron: form })
  );
});
